import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Building2,
  Users,
  Settings,
  Shield,
  Eye,
  EyeOff,
  Save,
  Plus,
  Edit,
  Trash2,
  UserPlus,
  Key,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  Activity,
  HardDrive,
  CheckCircle,
  AlertTriangle,
  Clock,
  XCircle,
  RefreshCw,
  AlertCircle,
  Info,
  Globe,
  Database,
  CreditCard,
  Zap,
  UserCheck,
  UserX
} from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';
import { AddUserModal, EditUserModal, DeleteUserModal } from '../firmAdmin/UserModals';
import { planService } from '../../services/plan.service';
import { tenantPlanService } from '../../services/tenantPlan.service';

interface TenantManageModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
  onTenantUpdate?: () => void;
  isPlatformTenant?: boolean; // Whether this is a platform tenant or regular tenant
}

interface TenantDetails {
  id: string;
  name: string;
  subdomain: string;
  plan: string;
  status: string;
  customDomain?: string;
  billing: {
    status: string;
    customerId?: string;
    subscriptionId?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    trialEnd?: string;
    cancelAtPeriodEnd: boolean;
    lastPaymentDate?: string;
    nextBillingDate?: string;
    totalRevenue: number;
    currency: string;
  };
  usage: {
    users: {
      current: number;
      limit: number;
    };
    storage: {
      current: number;
      limit: number;
    };
    apiCalls: {
      current: number;
      limit: number;
      resetDate: string;
    };
    features: string[];
  };
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    company?: string;
  };
  config: {
    timezone: string;
    locale: string;
    dateFormat: string;
    currency: string;
    allowedIPs?: string[];
    ssoEnabled: boolean;
    mfaRequired: boolean;
    passwordPolicy: {
      minLength: number;
      requireUppercase: boolean;
      requireLowercase: boolean;
      requireNumbers: boolean;
      requireSymbols: boolean;
      maxAge: number;
    };
  };
  metadata: {
    source: string;
    salesRep?: string;
    accountManager?: string;
    industry?: string;
    companySize?: string;
    referralSource?: string;
    notes?: string;
    tags: string[];
  };
  trialStartedAt?: string;
  activatedAt?: string;
  suspendedAt?: string;
  cancelledAt?: string;
  lastActivityAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface TenantUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  roles: string[]; // For compatibility with UserModals
  status: 'active' | 'invited' | 'disabled';
  lastLogin?: string;
  lastLoginAt?: string; // For compatibility with UserModals
  createdAt: string;
}

interface TenantUpdateUserData {
  firstName: string;
  lastName: string;
  roles: string[];
  status: 'active' | 'invited' | 'disabled';
}

interface TenantSettings {
  id: string;
  tenantId: string;
  allowRegistration: boolean;
  requireEmailVerification: boolean;
  maxUsers: number;
  sessionTimeout: number;
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
  };
  features: {
    hrModule: boolean;
    payrollModule: boolean;
    attendanceModule: boolean;
    leaveModule: boolean;
    performanceModule: boolean;
  };
}

interface AdminCredentials {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  status: string;
  lastLoginAt?: string;
}

interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  password?: string;
  sendInvitation?: boolean;
}



interface LoadingState {
  overview: boolean;
  users: boolean;
  settings: boolean;
  admin: boolean;
}

interface ErrorState {
  overview: string | null;
  users: string | null;
  settings: string | null;
  admin: string | null;
}

// Helper functions for status display
const getStatusColor = (status: string | undefined | null) => {
  if (!status) return 'bg-gray-100 text-gray-800';

  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'trial':
      return 'bg-yellow-100 text-yellow-800';
    case 'suspended':
      return 'bg-red-100 text-red-800';
    case 'inactive':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status: string | undefined | null) => {
  if (!status) return <Clock className="w-3 h-3 mr-1" />;

  switch (status.toLowerCase()) {
    case 'active':
      return <CheckCircle className="w-3 h-3 mr-1" />;
    case 'trial':
      return <Clock className="w-3 h-3 mr-1" />;
    case 'suspended':
      return <XCircle className="w-3 h-3 mr-1" />;
    case 'inactive':
      return <AlertTriangle className="w-3 h-3 mr-1" />;
    default:
      return <AlertTriangle className="w-3 h-3 mr-1" />;
  }
};

const getRoleColor = (role: string | undefined | null) => {
  if (!role) return 'bg-gray-100 text-gray-800';

  switch (role.toLowerCase()) {
    case 'admin':
    case 'firm_admin':
      return 'bg-purple-100 text-purple-800';
    case 'manager':
      return 'bg-blue-100 text-blue-800';
    case 'employee':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Helper function to get user role from either role string or roles array
const getUserRole = (user: TenantUser): string => {
  // If user has a role property, use it
  if (user.role) {
    return user.role;
  }

  // If user has roles array, get the first role or the highest priority role
  if (user.roles && user.roles.length > 0) {
    // Priority order: FIRM_ADMIN > MANAGER > EMPLOYEE
    if (user.roles.includes('FIRM_ADMIN')) return 'FIRM_ADMIN';
    if (user.roles.includes('MANAGER')) return 'MANAGER';
    if (user.roles.includes('EMPLOYEE')) return 'EMPLOYEE';
    if (user.roles.includes('ADMIN')) return 'ADMIN';

    // Return the first role if no priority match
    return user.roles[0];
  }

  return 'UNKNOWN';
};

// These constants are replaced by the helper functions above

export function TenantManageModal({ isOpen, onClose, tenantId, tenantName, onTenantUpdate, isPlatformTenant = false }: TenantManageModalProps) {
  console.log('TenantManageModal rendered:', { isOpen, tenantId, tenantName, isPlatformTenant });
  const isMountedRef = useRef(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [tenantDetails, setTenantDetails] = useState<TenantDetails | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [adminCredentials, setAdminCredentials] = useState<AdminCredentials | null>(null);

  // Plan management state
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [planHistory, setPlanHistory] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planNotes, setPlanNotes] = useState('');
  const [assigningPlan, setAssigningPlan] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Loading states for each tab
  const [loading, setLoading] = useState<LoadingState>({
    overview: false,
    users: false,
    settings: false,
    admin: false
  });

  // Error states for each tab
  const [errors, setErrors] = useState<ErrorState>({
    overview: null,
    users: null,
    settings: null,
    admin: null
  });

  // Track which tabs have been fetched to prevent infinite loops
  const [fetchedTabs, setFetchedTabs] = useState<Set<string>>(new Set());

  // User management modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [userActionLoading, setUserActionLoading] = useState(false);

  // Set password modal states
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [setPasswordValue, setSetPasswordValue] = useState('');
  const [showSetPasswordInput, setShowSetPasswordInput] = useState(false);
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [statusToggleLoading, setStatusToggleLoading] = useState<string | null>(null);

  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Helper functions to get correct API endpoints
  const getApiEndpoint = (endpoint: string) => {
    if (isPlatformTenant) {
      return `/platform/tenants/${tenantId}${endpoint}`;
    } else {
      // For regular tenants, use tenant-specific endpoints
      return `/tenants/${tenantId}${endpoint}`;
    }
  };

  // Initialize modal when opened
  useEffect(() => {
    if (isOpen && tenantId) {
      // Reset all states
      setTenantDetails(null);
      setTenantUsers([]);
      setTenantSettings(null);
      setAdminCredentials(null);
      setErrors({
        overview: null,
        users: null,
        settings: null,
        admin: null
      });
      setActiveTab('overview');
    }
  }, [isOpen, tenantId]);

  // Clear error for a specific tab
  const clearError = useCallback((tab: keyof ErrorState) => {
    setErrors(prev => ({ ...prev, [tab]: null }));
  }, []);

  // Set loading state for a specific tab
  const setTabLoading = useCallback((tab: keyof LoadingState, isLoading: boolean) => {
    setLoading(prev => ({ ...prev, [tab]: isLoading }));
  }, []);

  // Set error state for a specific tab
  const setTabError = useCallback((tab: keyof ErrorState, error: string | null) => {
    setErrors(prev => ({ ...prev, [tab]: error }));
  }, []);

  // Fetch tenant details (Overview tab)
  const fetchTenantDetails = useCallback(async () => {
    if (!tenantId || !isMountedRef.current) return;

    try {
      if (!isMountedRef.current) return;
      setTabLoading('overview', true);
      clearError('overview');

      const endpoint = isPlatformTenant ? `/platform/tenants/${tenantId}` : `/tenants/${tenantId}`;
      const response = await api.get(endpoint);

      if (!isMountedRef.current) return;

      if (response.data.success) {
        setTenantDetails(response.data.data);
      } else {
        const errorMsg = response.data.message || 'Failed to fetch tenant details';
        setTabError('overview', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error: any) {
      if (!isMountedRef.current) return;
      console.error('Error fetching tenant details:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch tenant details';
      setTabError('overview', errorMsg);
      toast.error(errorMsg);
    } finally {
      if (isMountedRef.current) {
        setTabLoading('overview', false);
      }
    }
  }, [tenantId, setTabLoading, clearError, setTabError]);

  // Fetch tenant users (Users tab)
  const fetchTenantUsers = useCallback(async () => {
    if (!tenantId || !isMountedRef.current) return;

    try {
      if (!isMountedRef.current) return;
      setTabLoading('users', true);
      clearError('users');
      console.log('Fetching tenant users for ID:', tenantId);

      const endpoint = isPlatformTenant ? `/platform/tenants/${tenantId}/users` : `/tenants/${tenantId}/users`;
      const response = await api.get(endpoint);
      console.log('Tenant users response:', response.data);

      if (!isMountedRef.current) return;

      if (response.data.success) {
        // Handle different response formats
        const users = response.data.data?.users || response.data.data || [];
        setTenantUsers(users);
      } else {
        const errorMsg = response.data.message || 'Failed to fetch tenant users';
        setTabError('users', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error: any) {
      if (!isMountedRef.current) return;
      console.error('Error fetching tenant users:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch tenant users';
      setTabError('users', errorMsg);
      toast.error(errorMsg);
    } finally {
      if (isMountedRef.current) {
        setTabLoading('users', false);
      }
    }
  }, [tenantId, setTabLoading, clearError, setTabError]);

  // Fetch tenant settings (Settings tab)
  const fetchTenantSettings = useCallback(async () => {
    if (!tenantId || !isMountedRef.current) return;

    try {
      if (!isMountedRef.current) return;
      setTabLoading('settings', true);
      clearError('settings');
      console.log('Fetching tenant settings for ID:', tenantId);

      const endpoint = isPlatformTenant ? `/platform/tenants/${tenantId}/settings` : `/tenants/${tenantId}/settings`;
      const response = await api.get(endpoint);
      console.log('Tenant settings response:', response.data);

      if (!isMountedRef.current) return;

      if (response.data.success) {
        setTenantSettings(response.data.data);
      } else {
        const errorMsg = response.data.message || 'Failed to fetch tenant settings';
        setTabError('settings', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error: any) {
      if (!isMountedRef.current) return;
      console.error('Error fetching tenant settings:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch tenant settings';
      setTabError('settings', errorMsg);
      toast.error(errorMsg);
    } finally {
      if (isMountedRef.current) {
        setTabLoading('settings', false);
      }
    }
  }, [tenantId, setTabLoading, clearError, setTabError]);

  // Fetch admin credentials (Admin tab)
  const fetchAdminCredentials = useCallback(async () => {
    if (!tenantId || !isMountedRef.current) return;

    try {
      if (!isMountedRef.current) return;
      setTabLoading('admin', true);
      clearError('admin');
      console.log('Fetching admin credentials for ID:', tenantId);

      if (isPlatformTenant) {
        // For platform tenants, use the platform admin endpoint
        const response = await api.get(`/platform/tenants/${tenantId}/admin`);
        console.log('Platform admin credentials response:', response.data);
        console.log('Platform admin credentials data:', response.data.data);

        if (!isMountedRef.current) return;

        if (response.data.success) {
          console.log('Setting admin credentials:', response.data.data);
          if (response.data.data === null) {
            console.log('API returned null - no admin user found for this tenant');
            setAdminCredentials(null);
          } else {
            setAdminCredentials(response.data.data);
          }
          setFetchedTabs(prev => new Set(prev).add('admin'));
        } else {
          const errorMsg = response.data.message || 'Failed to fetch admin credentials';
          console.log('Admin credentials error:', errorMsg);
          setTabError('admin', errorMsg);
          toast.error(errorMsg);
        }
      } else {
        // For regular tenants, get the admin user from the users list
        const response = await api.get(`/tenants/${tenantId}/users`);
        console.log('Regular tenant users response:', response.data);

        if (!isMountedRef.current) return;

        if (response.data.success) {
          const users = response.data.data?.users || response.data.data || [];
          // Find the admin user (FIRM_ADMIN role)
          const adminUser = users.find((user: any) =>
            user.roles?.includes('FIRM_ADMIN') || user.role === 'FIRM_ADMIN'
          );

          if (adminUser) {
            setAdminCredentials({
              id: adminUser.id,
              email: adminUser.email,
              firstName: adminUser.firstName,
              lastName: adminUser.lastName,
              fullName: `${adminUser.firstName} ${adminUser.lastName}`,
              status: adminUser.status,
              lastLoginAt: adminUser.lastLoginAt
            });
          } else {
            setAdminCredentials(null);
          }
          setFetchedTabs(prev => new Set(prev).add('admin'));
        } else {
          const errorMsg = response.data.message || 'Failed to fetch admin credentials';
          setTabError('admin', errorMsg);
          toast.error(errorMsg);
        }
      }
    } catch (error: any) {
      if (!isMountedRef.current) return;
      console.error('Error fetching admin credentials:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch admin credentials';
      setTabError('admin', errorMsg);
      toast.error(errorMsg);
    } finally {
      if (isMountedRef.current) {
        setTabLoading('admin', false);
      }
    }
  }, [tenantId, isPlatformTenant, setTabLoading, clearError, setTabError]);

  // Load plan data
  const loadPlanData = useCallback(async () => {
    try {
      // Load available plans
      const plansResponse = await planService.getAllPlans(true);
      setAvailablePlans(plansResponse.data);

      // Load current plan for tenant
      try {
        const currentPlanResponse = await tenantPlanService.getCurrentPlanByTenant(tenantId);
        setCurrentPlan(currentPlanResponse.data);
      } catch (err) {
        // No current plan is OK
        setCurrentPlan(null);
      }

      // Load plan history
      const historyResponse = await tenantPlanService.getTenantPlanHistory(tenantId);
      setPlanHistory(historyResponse.data);
    } catch (error: any) {
      console.error('Error loading plan data:', error);
      toast.error('Failed to load plan information');
    }
  }, [tenantId]);

  const handleStatusChange = useCallback(async (action: 'suspend' | 'reactivate') => {
    if (!tenantId) return;

    try {
      setStatusUpdating(true);
      const endpointBase = isPlatformTenant ? `/platform/tenants/${tenantId}` : `/tenants/${tenantId}`;
      const url = `${endpointBase}/${action === 'suspend' ? 'suspend' : 'reactivate'}`;
      const payload = action === 'suspend' ? { reason: 'Suspended by platform admin' } : undefined;

      const response = await api.post(url, payload);

      if (!response.data?.success) {
        throw new Error(response.data?.message || `Failed to ${action} tenant`);
      }

      const nextStatus = action === 'reactivate' ? 'active' : 'suspended';
      setTenantDetails(prev => (prev ? { ...prev, status: nextStatus } : prev));
      toast.success(action === 'reactivate' ? 'Tenant reactivated successfully' : 'Tenant suspended successfully');

      // Refresh upstream state so the list stays in sync
      onTenantUpdate?.();
      fetchTenantDetails();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Unable to update tenant status';
      toast.error(errorMsg);
    } finally {
      setStatusUpdating(false);
    }
  }, [tenantId, isPlatformTenant, onTenantUpdate, fetchTenantDetails]);

  // Load data when modal opens or tab changes
  useEffect(() => {
    if (!isOpen || !tenantId) return;

    switch (activeTab) {
      case 'overview':
        if (!tenantDetails && !loading.overview) {
          fetchTenantDetails();
        }
        break;
      case 'plan':
        if (availablePlans.length === 0) {
          loadPlanData();
        }
        break;
      case 'users':
        if (tenantUsers.length === 0 && !loading.users) {
          fetchTenantUsers();
        }
        break;
      case 'settings':
        if (!tenantSettings && !loading.settings) {
          fetchTenantSettings();
        }
        break;
      case 'admin':
        if (!fetchedTabs.has('admin') && !loading.admin) {
          fetchAdminCredentials();
        }
        break;
    }
  }, [activeTab, isOpen, tenantId]); // Removed state dependencies to prevent infinite loops

  // Retry function for failed requests
  const retryFetch = useCallback((tab: string) => {
    // Clear the fetched flag for the tab being retried
    setFetchedTabs(prev => {
      const newSet = new Set(prev);
      newSet.delete(tab);
      return newSet;
    });

    switch (tab) {
      case 'overview':
        fetchTenantDetails();
        break;
      case 'users':
        fetchTenantUsers();
        break;
      case 'settings':
        fetchTenantSettings();
        break;
      case 'admin':
        fetchAdminCredentials();
        break;
    }
  }, [fetchTenantDetails, fetchTenantUsers, fetchTenantSettings, fetchAdminCredentials]);

  // User management functions
  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      setUserActionLoading(true);
      console.log('Creating user for tenant:', tenantId);
      console.log('User data being sent:', JSON.stringify(userData, null, 2));
      console.log('API endpoint:', `/platform/tenants/${tenantId}/users`);

      const endpoint = isPlatformTenant ? `/platform/tenants/${tenantId}/users` : `/tenants/${tenantId}/users`;
      const response = await api.post(endpoint, userData);

      if (response.data.success) {
        toast.success('User created successfully');
        setShowAddUserModal(false);
        // Refresh the users list
        fetchTenantUsers();
      } else {
        throw new Error(response.data.message || 'Failed to create user');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);

      // Extract error message from the API response format
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to create user';

      toast.error(errorMessage);
    } finally {
      setUserActionLoading(false);
    }
  };

  const handleUpdateUser = async (userId: string, userData: TenantUpdateUserData) => {
    try {
      setUserActionLoading(true);
      console.log('Updating user:', userId, userData);

      const endpoint = isPlatformTenant ? `/platform/tenants/${tenantId}/users/${userId}` : `/tenants/${tenantId}/users/${userId}`;
      const response = await api.patch(endpoint, userData);

      if (response.data.success) {
        toast.success('User updated successfully');
        setShowEditUserModal(false);
        setSelectedUser(null);
        // Refresh the users list
        fetchTenantUsers();
      } else {
        throw new Error(response.data.message || 'Failed to update user');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);

      // Extract error message from the API response format
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to update user';

      toast.error(errorMessage);
    } finally {
      setUserActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setUserActionLoading(true);
      console.log('Deleting user:', userId);

      const endpoint = isPlatformTenant ? `/platform/tenants/${tenantId}/users/${userId}` : `/tenants/${tenantId}/users/${userId}`;
      const response = await api.delete(endpoint);

      if (response.data.success) {
        toast.success('User deleted successfully');
        setShowDeleteUserModal(false);
        setSelectedUser(null);
        // Refresh the users list
        fetchTenantUsers();
      } else {
        throw new Error(response.data.message || 'Failed to delete user');
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);

      // Extract error message from the API response format
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to delete user';

      toast.error(errorMessage);
    } finally {
      setUserActionLoading(false);
    }
  };

  const handleToggleUserStatus = async (user: TenantUser) => {
    const newStatus = user.status === 'active' ? 'disabled' : 'active';
    try {
      setStatusToggleLoading(user.id);
      const endpoint = isPlatformTenant
        ? `/platform/tenants/${tenantId}/users/${user.id}`
        : `/tenants/${tenantId}/users/${user.id}`;
      const response = await api.patch(endpoint, { status: newStatus });
      if (response.data.success) {
        toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        fetchTenantUsers();
      } else {
        throw new Error(response.data.message || 'Failed to update user status');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to update user status';
      toast.error(errorMessage);
    } finally {
      setStatusToggleLoading(null);
    }
  };

  const handleSetUserPassword = async () => {
    if (!selectedUser || !setPasswordValue) return;
    try {
      setSetPasswordLoading(true);
      const endpoint = isPlatformTenant
        ? `/platform/tenants/${tenantId}/users/${selectedUser.id}`
        : `/tenants/${tenantId}/users/${selectedUser.id}`;
      const response = await api.patch(endpoint, { password: setPasswordValue });
      if (response.data.success) {
        toast.success('Password updated successfully');
        setShowSetPasswordModal(false);
        setSetPasswordValue('');
        setShowSetPasswordInput(false);
        setSelectedUser(null);
      } else {
        throw new Error(response.data.message || 'Failed to update password');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to update password';
      toast.error(errorMessage);
    } finally {
      setSetPasswordLoading(false);
    }
  };

  // Reset state when modal opens or tenant changes
  useEffect(() => {
    if (isOpen && tenantId) {
      console.log('Modal opened for tenant:', tenantId);
      isMountedRef.current = true;
      // Reset all state when modal opens
      setTenantDetails(null);
      setTenantUsers([]);
      setTenantSettings(null);
      setAdminCredentials(null);
      setFetchedTabs(new Set());
      setLoading({
        overview: false,
        users: false,
        settings: false,
        admin: false
      });
      setErrors({
        overview: null,
        users: null,
        settings: null,
        admin: null
      });
    }
  }, [isOpen, tenantId]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!isOpen) {
    console.log('Modal not open, returning null');
    return null;
  }

  console.log('Modal is open, rendering...');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Building2 className="w-6 h-6 text-primary-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Manage Tenant</h2>
              <p className="text-sm text-gray-500">{tenantName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'plan', label: 'Plan & Billing', icon: CreditCard },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'settings', label: 'Settings', icon: Settings },
              { id: 'admin', label: 'Admin Access', icon: Shield }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {loading.overview ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600">Loading tenant details...</span>
                </div>
              ) : errors.overview ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load tenant details</h3>
                  <p className="text-gray-600 mb-4 text-center">{errors.overview}</p>
                  <button
                    onClick={() => retryFetch('overview')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              ) : tenantDetails ? (
                <>
                  {/* Basic Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <Building2 className="w-5 h-5 mr-2 text-primary-600" />
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tenant Name</label>
                        <p className="text-sm text-gray-900 font-medium">{tenantDetails.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
                        <div className="flex items-center">
                          <Globe className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">{tenantDetails.subdomain}.erpca.ca</p>
                        </div>
                      </div>
                      {tenantDetails.customDomain && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Custom Domain</label>
                          <div className="flex items-center">
                            <Globe className="w-4 h-4 text-gray-400 mr-2" />
                            <p className="text-sm text-gray-900">{tenantDetails.customDomain}</p>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <CreditCard className="w-3 h-3 mr-1" />
                          {tenantDetails.plan}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tenantDetails.status)}`}>
                            {getStatusIcon(tenantDetails.status)}
                            {tenantDetails.status}
                          </span>
                          {!isPlatformTenant && (
                            tenantDetails.status?.toLowerCase() === 'suspended' ? (
                              <button
                                onClick={() => handleStatusChange('reactivate')}
                                disabled={statusUpdating}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-60"
                              >
                                {statusUpdating ? 'Updating...' : 'Reactivate Tenant'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange('suspend')}
                                disabled={statusUpdating}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-yellow-100 text-yellow-800 hover:bg-yellow-200 disabled:opacity-60"
                              >
                                {statusUpdating ? 'Updating...' : 'Suspend Tenant'}
                              </button>
                            )
                          )}
                        </div>
                        {!isPlatformTenant && tenantDetails.status?.toLowerCase() === 'suspended' && (
                          <p className="text-xs text-red-600 mt-1">Tenant is suspended. Reactivate to restore access for firm users.</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">{new Date(tenantDetails.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Usage Statistics */}
                  {tenantDetails.usage && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                        <Activity className="w-5 h-5 mr-2 text-primary-600" />
                        Usage Statistics
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Users</label>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-gray-900">{tenantDetails.usage.users.current}</span>
                            <span className="text-sm text-gray-500">/ {tenantDetails.usage.users.limit}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${Math.min((tenantDetails.usage.users.current / tenantDetails.usage.users.limit) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Storage</label>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-gray-900">{Math.round(tenantDetails.usage.storage.current / 1024 / 1024)}MB</span>
                            <span className="text-sm text-gray-500">/ {Math.round(tenantDetails.usage.storage.limit / 1024 / 1024)}MB</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{ width: `${Math.min((tenantDetails.usage.storage.current / tenantDetails.usage.storage.limit) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">API Calls</label>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-gray-900">{tenantDetails.usage.apiCalls.current.toLocaleString()}</span>
                            <span className="text-sm text-gray-500">/ {tenantDetails.usage.apiCalls.limit.toLocaleString()}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full"
                              style={{ width: `${Math.min((tenantDetails.usage.apiCalls.current / tenantDetails.usage.apiCalls.limit) * 100, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  {tenantDetails.contact && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                        <Mail className="w-5 h-5 mr-2 text-primary-600" />
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Primary Contact</label>
                          <p className="text-sm text-gray-900">{tenantDetails.contact.firstName} {tenantDetails.contact.lastName}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 text-gray-400 mr-2" />
                            <p className="text-sm text-gray-900">{tenantDetails.contact.email}</p>
                          </div>
                        </div>
                        {tenantDetails.contact.phone && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 text-gray-400 mr-2" />
                              <p className="text-sm text-gray-900">{tenantDetails.contact.phone}</p>
                            </div>
                          </div>
                        )}
                        {tenantDetails.contact.company && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                            <p className="text-sm text-gray-900">{tenantDetails.contact.company}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Features */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-primary-600" />
                      Enabled Features
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {tenantDetails.usage?.features && tenantDetails.usage.features.length > 0 ? (
                        tenantDetails.usage.features.map((feature) => (
                          <div key={feature} className="flex items-center">
                            <div className="w-3 h-3 rounded-full mr-2 bg-green-500"></div>
                            <span className="text-sm text-gray-700 capitalize">{feature.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}</span>
                          </div>
                        ))
                      ) : (
                        <div className="col-span-full text-sm text-gray-500">
                          No features enabled
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Info className="w-12 h-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No tenant data available</h3>
                  <p className="text-gray-600 mb-4">Unable to load tenant information.</p>
                  <button
                    onClick={() => retryFetch('overview')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Plan & Billing Tab */}
          {activeTab === 'plan' && (
            <div className="space-y-6">
              {/* Current Plan */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-primary-600" />
                  Current Plan
                </h3>
                {currentPlan ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{currentPlan.planName}</h4>
                        <p className="text-sm text-gray-600 mt-1">{currentPlan.description || 'No description'}</p>
                        <div className="mt-4 grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-500">Start Date</span>
                            <p className="text-sm font-medium text-gray-900">{new Date(currentPlan.startDate).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">End Date</span>
                            <p className="text-sm font-medium text-gray-900">{new Date(currentPlan.endDate).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Status</span>
                            <p className="text-sm font-medium capitalize">{currentPlan.status}</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Payment Method</span>
                            <p className="text-sm font-medium capitalize">{currentPlan.paymentMethod}</p>
                          </div>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-gray-900">
                        ${currentPlan.cost} <span className="text-sm text-gray-600">/{currentPlan.duration}</span>
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">No active plan assigned</p>
                  </div>
                )}
              </div>

              {/* Assign New Plan */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Plus className="w-5 h-5 mr-2 text-primary-600" />
                  Assign New Plan
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="plan-select" className="block text-sm font-medium text-gray-700 mb-2">
                      Select Plan
                    </label>
                    <select
                      id="plan-select"
                      value={selectedPlanId}
                      onChange={(e) => setSelectedPlanId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">-- Select a plan --</option>
                      {availablePlans.map((plan) => (
                        <option key={plan._id} value={plan._id}>
                          {plan.displayName} - ${plan.cost} CAD/{plan.duration} ({plan.maxUsers} users, {plan.maxClients} clients)
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedPlanId && (
                    <>
                      <div>
                        <label htmlFor="plan-notes" className="block text-sm font-medium text-gray-700 mb-2">
                          Notes (Optional)
                        </label>
                        <textarea
                          id="plan-notes"
                          rows={3}
                          value={planNotes}
                          onChange={(e) => setPlanNotes(e.target.value)}
                          placeholder="Add any notes about this plan assignment..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex">
                          <Info className="w-5 h-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-700">
                            <p className="font-medium mb-1">Plan Assignment</p>
                            <p>This will assign the selected plan to the tenant starting today. If there's an active plan, it will be marked as expired.</p>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={async () => {
                          if (!selectedPlanId || !tenantId) return;
                          
                          setAssigningPlan(true);
                          try {
                            await tenantPlanService.assignPlanToTenant(tenantId, selectedPlanId, new Date(), planNotes);
                            toast.success('Plan assigned successfully');
                            setSelectedPlanId('');
                            setPlanNotes('');
                            // Reload plan data
                            await loadPlanData();
                            if (onTenantUpdate) onTenantUpdate();
                          } catch (error: any) {
                            toast.error(error.response?.data?.message || 'Failed to assign plan');
                          } finally {
                            setAssigningPlan(false);
                          }
                        }}
                        disabled={assigningPlan || !selectedPlanId}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {assigningPlan ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Assigning Plan...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Assign Plan
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Plan History */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-primary-600" />
                  Plan History
                </h3>
                {planHistory.length > 0 ? (
                  <div className="space-y-3">
                    {planHistory.map((plan, index) => (
                      <div
                        key={plan._id}
                        className={`border rounded-lg p-4 ${
                          plan.status === 'active' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="text-sm font-medium text-gray-900">{plan.planName}</h4>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                plan.status === 'active' ? 'bg-green-100 text-green-800' :
                                plan.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {plan.status}
                              </span>
                            </div>
                            <div className="mt-2 grid grid-cols-3 gap-4 text-xs text-gray-600">
                              <div>
                                <span className="font-medium">Period:</span>{' '}
                                {new Date(plan.startDate).toLocaleDateString()} - {new Date(plan.endDate).toLocaleDateString()}
                              </div>
                              <div>
                                <span className="font-medium">Cost:</span> ${plan.cost} {plan.currency}/{plan.duration}
                              </div>
                              <div>
                                <span className="font-medium">Payment:</span> {plan.paymentMethod}
                              </div>
                            </div>
                            {plan.notes && (
                              <p className="mt-2 text-xs text-gray-600 italic">{plan.notes}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-gray-500">
                    No plan history available
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              {loading.users ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600">Loading users...</span>
                </div>
              ) : errors.users ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load users</h3>
                  <p className="text-gray-600 mb-4 text-center">{errors.users}</p>
                  <button
                    onClick={() => retryFetch('users')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-primary-600" />
                      Tenant Users ({tenantUsers.length})
                    </h3>
                    <button
                      onClick={() => setShowAddUserModal(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                    </button>
                  </div>

                  {tenantUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Users className="w-12 h-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                      <p className="text-gray-600">This tenant doesn't have any users yet.</p>
                    </div>
                  ) : (
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                          {tenantUsers.map((user) => (
                            <li key={user.id} className="px-6 py-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0">
                                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                      <span className="text-sm font-medium text-primary-700">
                                        {user.firstName?.[0] || ''}{user.lastName?.[0] || ''}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {user.firstName || ''} {user.lastName || ''}
                                    </div>
                                    <div className="text-sm text-gray-500 flex items-center">
                                      <Mail className="w-3 h-3 mr-1" />
                                      {user.email || 'No email'}
                                    </div>
                                    {user.lastLogin && (
                                      <div className="text-xs text-gray-400 mt-1">
                                        Last login: {new Date(user.lastLogin).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                  <div className="text-right">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(getUserRole(user))}`}>
                                      {getUserRole(user).replace('_', ' ').toUpperCase()}
                                    </span>
                                    <div className="text-xs text-gray-500 mt-1">
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                                        {user.status || 'unknown'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setShowEditUserModal(true);
                                      }}
                                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                                      title="Edit user"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleToggleUserStatus(user)}
                                      disabled={statusToggleLoading === user.id}
                                      className={`p-1 rounded transition-colors ${
                                        user.status === 'active'
                                          ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                                          : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                      }`}
                                      title={user.status === 'active' ? 'Deactivate user' : 'Activate user'}
                                    >
                                      {statusToggleLoading === user.id ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                      ) : user.status === 'active' ? (
                                        <UserX className="w-4 h-4" />
                                      ) : (
                                        <UserCheck className="w-4 h-4" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setSetPasswordValue('');
                                        setShowSetPasswordInput(false);
                                        setShowSetPasswordModal(true);
                                      }}
                                      className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                                      title="Set password"
                                    >
                                      <Key className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setSelectedUser(user);
                                        setShowDeleteUserModal(true);
                                      }}
                                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                      title="Delete user"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {loading.settings ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600">Loading settings...</span>
                </div>
              ) : errors.settings ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load settings</h3>
                  <p className="text-gray-600 mb-4 text-center">{errors.settings}</p>
                  <button
                    onClick={() => retryFetch('settings')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              ) : tenantSettings ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Settings className="w-5 h-5 mr-2 text-primary-600" />
                    Tenant Settings
                  </h3>

                  {/* General Settings */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">General Settings</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
                        <p className="text-sm text-gray-900">{tenantSettings.maxUsers}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Session Timeout</label>
                        <p className="text-sm text-gray-900">{tenantSettings.sessionTimeout} minutes</p>
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={tenantSettings.allowRegistration}
                            readOnly
                            className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Allow Registration</span>
                        </label>
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={tenantSettings.requireEmailVerification}
                            readOnly
                            className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                          />
                          <span className="ml-2 text-sm text-gray-700">Require Email Verification</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Password Policy */}
                  {tenantSettings.passwordPolicy && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                        <Key className="w-4 h-4 mr-2" />
                        Password Policy
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Length</label>
                          <p className="text-sm text-gray-900">{tenantSettings.passwordPolicy.minLength} characters</p>
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={tenantSettings.passwordPolicy.requireUppercase}
                              readOnly
                              className="rounded border-gray-300 text-primary-600 shadow-sm"
                            />
                            <span className="ml-2 text-sm text-gray-700">Require Uppercase</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={tenantSettings.passwordPolicy.requireLowercase}
                              readOnly
                              className="rounded border-gray-300 text-primary-600 shadow-sm"
                            />
                            <span className="ml-2 text-sm text-gray-700">Require Lowercase</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={tenantSettings.passwordPolicy.requireNumbers}
                              readOnly
                              className="rounded border-gray-300 text-primary-600 shadow-sm"
                            />
                            <span className="ml-2 text-sm text-gray-700">Require Numbers</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={tenantSettings.passwordPolicy.requireSpecialChars}
                              readOnly
                              className="rounded border-gray-300 text-primary-600 shadow-sm"
                            />
                            <span className="ml-2 text-sm text-gray-700">Require Special Characters</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Feature Modules */}
                  {tenantSettings.features && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                        <Zap className="w-4 h-4 mr-2" />
                        Feature Modules
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {tenantSettings.features && Object.entries(tenantSettings.features).map(([key, value]) => (
                          <label key={key} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={value}
                              readOnly
                              className="rounded border-gray-300 text-primary-600 shadow-sm"
                            />
                            <span className="ml-2 text-sm text-gray-700 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').replace('Module', '').trim()}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Settings className="w-12 h-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No settings available</h3>
                  <p className="text-gray-600 mb-4">Unable to load tenant settings.</p>
                  <button
                    onClick={() => retryFetch('settings')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === 'admin' && (
            <div className="space-y-6">
              {loading.admin ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600">Loading admin information...</span>
                </div>
              ) : errors.admin ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load admin information</h3>
                  <p className="text-gray-600 mb-4 text-center">{errors.admin}</p>
                  <button
                    onClick={() => retryFetch('admin')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                  </button>
                </div>
              ) : adminCredentials ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-primary-600" />
                    Admin Access
                  </h3>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Tenant Administrator</h4>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Shield className="w-6 h-6 text-purple-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                            <p className="text-sm text-gray-900 font-medium">{adminCredentials?.fullName || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <div className="flex items-center">
                              <Mail className="w-4 h-4 text-gray-400 mr-2" />
                              <p className="text-sm text-gray-900">{adminCredentials?.email || 'N/A'}</p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${adminCredentials?.status ? getStatusColor(adminCredentials.status) : 'bg-gray-100 text-gray-800'}`}>
                              {adminCredentials?.status ? getStatusIcon(adminCredentials.status) : null}
                              {adminCredentials?.status || 'Unknown'}
                            </span>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Login</label>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 text-gray-400 mr-2" />
                              <p className="text-sm text-gray-900">
                                {adminCredentials?.lastLoginAt
                                  ? new Date(adminCredentials.lastLoginAt).toLocaleString()
                                  : 'Never'
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Admin Actions */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Admin Actions</h4>
                    <div className="space-y-3">
                      <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        <Key className="w-4 h-4 mr-2" />
                        Reset Password
                      </button>
                      <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        <Mail className="w-4 h-4 mr-2" />
                        Send Login Instructions
                      </button>
                      <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        <Edit className="w-4 h-4 mr-2" />
                        Update Credentials
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Shield className="w-12 h-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No admin user found</h3>
                  <p className="text-gray-600 mb-4">This tenant doesn't have an admin user configured yet.</p>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => retryFetch('admin')}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry
                    </button>
                    <button
                      onClick={() => {
                        // TODO: Implement create admin user functionality
                        toast.info('Create admin user functionality coming soon');
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Admin User
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Management Modals */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSubmit={handleCreateUser}
        loading={userActionLoading}
      />

      <EditUserModal
        isOpen={showEditUserModal}
        onClose={() => {
          setShowEditUserModal(false);
          setSelectedUser(null);
        }}
        onSubmit={(userId: string, userData: any) => handleUpdateUser(userId, userData as TenantUpdateUserData)}
        user={selectedUser}
        loading={userActionLoading}
      />

      <DeleteUserModal
        isOpen={showDeleteUserModal}
        onClose={() => {
          setShowDeleteUserModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteUser}
        user={selectedUser}
        loading={userActionLoading}
      />

      {/* Set Password Modal */}
      {showSetPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Key className="w-5 h-5 mr-2 text-primary-600" />
                Set Password
              </h3>
              <button
                onClick={() => {
                  setShowSetPasswordModal(false);
                  setSetPasswordValue('');
                  setShowSetPasswordInput(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Setting new password for{' '}
              <span className="font-medium">{selectedUser.firstName} {selectedUser.lastName}</span>{' '}
              ({selectedUser.email})
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showSetPasswordInput ? 'text' : 'password'}
                  value={setPasswordValue}
                  onChange={(e) => setSetPasswordValue(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSetPasswordInput(!showSetPasswordInput)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showSetPasswordInput ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {setPasswordValue.length > 0 && setPasswordValue.length < 8 && (
                <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSetPasswordModal(false);
                  setSetPasswordValue('');
                  setShowSetPasswordInput(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSetUserPassword}
                disabled={setPasswordLoading || setPasswordValue.length < 8}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {setPasswordLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
